import { useMemo, useState, useCallback } from "react"
import { newPaymentLine } from "../constants"

const emptyHeader = {
  requesterRegion: "",
  region: "",
  budgetCategoryId: null,
  departmentId: null,
  paymentMethod: "bank_transfer",
  urgency: "normal",
  advanceAmount: "",
  reason: "",
  allocationInfo: "",
  poCode: "",
  poTotalAmount: 0,
  poAllowedSupplierIds: [],
  amountDifferencesExplanation: "",
}

export const useRequestForm = (initial) => {
  const [header, setHeader] = useState({ ...emptyHeader, ...(initial?.header || {}) })
  const [lines, setLines] = useState(
    initial?.lines && initial.lines.length > 0
      ? initial.lines
      : [newPaymentLine()]
  )

  const setHeaderField = useCallback((field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }))
  }, [])

  const updateLine = useCallback((idx, patch) => {
    setLines((prev) =>
      prev.map((line, i) => (i === idx ? { ...line, ...patch } : line))
    )
  }, [])

  const addLine = useCallback(() => {
    setLines((prev) => [...prev, newPaymentLine()])
  }, [])

  const duplicateLine = useCallback((idx) => {
    setLines((prev) => {
      const copy = { ...prev[idx] }
      return [...prev.slice(0, idx + 1), copy, ...prev.slice(idx + 1)]
    })
  }, [])

  const removeLine = useCallback((idx) => {
    setLines((prev) =>
      prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)
    )
  }, [])

  const totalAmountPR = useMemo(
    () => lines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0),
    [lines]
  )

  const totalAmountPO = Number(header.poTotalAmount) || 0
  const totalsDiffer =
    !!header.poCode && Math.abs(totalAmountPR - totalAmountPO) > 0.0001

  // §7.2 form-level validation
  const errors = useMemo(() => {
    const e = {}
    if (!header.requesterRegion) e.requesterRegion = "Required"
    if (!header.region) e.region = "Required"
    if (!header.budgetCategoryId) e.budgetCategoryId = "Required"
    if (!header.departmentId) e.departmentId = "Required"
    if (!header.paymentMethod) e.paymentMethod = "Required"
    if (!header.urgency) e.urgency = "Required"
    if (!header.reason?.trim()) e.reason = "Required"

    if (lines.length === 0) e.lines = "At least one payment line is required"
    const lineErrors = lines.map((line) => {
      const le = {}
      if (!line.supplierId) le.supplierId = "Required"
      if (line.paymentType !== "deposit" && !line.invoiceNumber)
        le.invoiceNumber = "Required unless Deposit"
      if (!(Number(line.amount) > 0)) le.amount = "Amount must be > 0"
      if (
        header.poCode &&
        header.poAllowedSupplierIds.length > 0 &&
        line.supplierId &&
        !header.poAllowedSupplierIds.includes(line.supplierId)
      ) {
        le.supplierId = "Supplier must belong to the linked PO"
      }
      if (!line.paymentType) le.paymentType = "Required"
      return le
    })
    if (lineErrors.some((le) => Object.keys(le).length > 0)) e.lineErrors = lineErrors

    if (totalsDiffer && !header.amountDifferencesExplanation?.trim()) {
      e.amountDifferencesExplanation =
        "Required when PR total differs from PO total"
    }
    return e
  }, [header, lines, totalsDiffer])

  const isValid = Object.keys(errors).length === 0

  const buildBody = useCallback(
    () => ({
      requesterRegion: header.requesterRegion,
      region: header.region,
      budgetCategoryId: header.budgetCategoryId,
      departmentId: header.departmentId,
      paymentMethod: header.paymentMethod,
      urgency: header.urgency,
      advanceAmount:
        header.advanceAmount === "" ? null : Number(header.advanceAmount),
      reason: header.reason,
      allocationInfo: header.allocationInfo,
      poCode: header.poCode || null,
      amountDifferencesExplanation: totalsDiffer
        ? header.amountDifferencesExplanation
        : null,
      totalAmount: totalAmountPR,
      paymentLines: lines.map((l) => ({
        supplierId: l.supplierId,
        invoiceSerial: l.invoiceSerial,
        invoiceNumber: l.invoiceNumber,
        invoiceDate: l.invoiceDate || null,
        supplierTaxCode: l.supplierTaxCode,
        description: l.description,
        amount: Number(l.amount) || 0,
        note: l.note,
        paymentType: l.paymentType,
        paymentTimeline: l.paymentTimeline || null,
        receiverName: l.receiverName,
        bankName: l.bankName,
        bankAccount: l.bankAccount,
      })),
    }),
    [header, lines, totalAmountPR, totalsDiffer]
  )

  return {
    header,
    lines,
    setHeader,
    setHeaderField,
    updateLine,
    addLine,
    duplicateLine,
    removeLine,
    totalAmountPR,
    totalAmountPO,
    totalsDiffer,
    errors,
    isValid,
    buildBody,
  }
}
